import { ContextualTipsTrigger } from "@babackapp/ui/components/contextual-tips";
import { cn } from "@babackapp/ui/lib/utils";
import { useTranslation } from "react-i18next";
import { Handle, type HandleProps, useNodeId } from "@xyflow/react";
import { Plus } from "lucide-react";
import { forwardRef, ForwardRefExoticComponent, RefAttributes } from "react";
import { useAddNodeContext } from "../context/add-node-context";
import { useIsWorkflowsListingMode } from "../context/workflows-editor-workflows-context";
import { useEditMode } from "../context/edit-mode-context";

export type BaseHandleProps = HandleProps;

const BaseHandleComponent = forwardRef<HTMLDivElement, BaseHandleProps>(
  function BaseHandle({ className, children, ...props }, ref) {
    const nodeId = useNodeId();
    const { t } = useTranslation("trackoffice");
    const isWorkflowsListingMode = useIsWorkflowsListingMode();
    const editMode = useEditMode();
    const canAddNode = editMode && !isWorkflowsListingMode;
    const isSourceHandle = props.type === "source";
    const { setAddNodeSource, sourceNodeId, sourceHandleId, isAddingNode } =
      useAddNodeContext();

    const handleId = props.id || "default";

    const handleButtonClick = (e: React.MouseEvent) => {
      e.stopPropagation();
      if (nodeId && canAddNode) {
        setAddNodeSource(nodeId, handleId);
        // Don't clear selection here - causes panel flash
        // The new node will be selected after creation anyway
      }
    };

    const isSelected =
      isSourceHandle &&
      isAddingNode &&
      nodeId === sourceNodeId &&
      handleId === sourceHandleId;

    return (
      <Handle
        ref={ref}
        className={cn(
          "h-[11px] w-[11px] rounded-full border transition",
          isSelected
            ? "border-purple-500 bg-purple-500 ring-2 ring-purple-300"
            : "border-slate-300 bg-slate-100 dark:border-secondary dark:bg-secondary",
          className,
        )}
        {...props}
      >
        {children}
        {isSourceHandle && canAddNode && (
          <ContextualTipsTrigger
            wrapped
            tips={[
              {
                id: "workflow-handle",
                title: t(
                  "workflowsEditor.tips.workflowHandle.title",
                  "Add a new node",
                ),
                description: t(
                  "workflowsEditor.tips.workflowHandle.desc",
                  "Click this button to add a new node to your workflow. The next node will be executed sequentially, after the previous one ends. You can add as many nodes as you need after an existing node.",
                ),
              },
            ]}
          >
            <button
              type="button"
              onClick={handleButtonClick}
              className={cn(
                "nodrag nopan absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 flex h-5 w-5 ml-[4px] items-center justify-center rounded-full border transition-colors cursor-pointer z-10",
                isSelected
                  ? "border-purple-500 bg-purple-500 text-white hover:border-purple-600 hover:bg-purple-600"
                  : "border-gray-400 bg-white text-gray-600 hover:border-blue-500 hover:text-blue-500",
              )}
            >
              <Plus size={10} />
            </button>
          </ContextualTipsTrigger>
        )}
      </Handle>
    );
  },
);

BaseHandleComponent.displayName = "BaseHandle";

export const BaseHandle: ForwardRefExoticComponent<
  BaseHandleProps & RefAttributes<HTMLDivElement>
> = BaseHandleComponent;
